import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { X, Trophy, ArrowLeftRight, Home, CreditCard, User, Briefcase, Calculator, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Logo } from './Logo';

export function Sidebar({ session }: { session: any }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const closeSidebar = () => setIsOpen(false);

  useEffect(() => {
    const handleToggle = () => setIsOpen((prev) => !prev);
    const handleOpen = () => setIsOpen(true);
    document.addEventListener('toggle-sidebar-drawer', handleToggle);
    document.addEventListener('open-sidebar-drawer', handleOpen);
    return () => {
      document.removeEventListener('toggle-sidebar-drawer', handleToggle);
      document.removeEventListener('open-sidebar-drawer', handleOpen);
    };
  }, []);

  const menuItems = [
    { path: '/', icon: Home, label: 'Terminal de Análise', code: '01' },
    { path: '/rankings', icon: Trophy, label: 'Rankings B3 & EUA', code: '02' },
    { path: '/compare', icon: ArrowLeftRight, label: 'Comparador de Ações', code: '03' },
    { path: '/portfolio', icon: Briefcase, label: 'Minha Carteira', code: '04' },
    { path: '/juros-compostos', icon: Calculator, label: 'Juros Compostos', code: '05' },
    { path: '/pricing', icon: CreditCard, label: 'Planos & Assinatura', code: '06' },
    {
      path: '/profile',
      icon: session?.user_metadata?.avatar_url ? 'avatar' : User,
      label: session ? (session.user_metadata?.username || session.user_metadata?.full_name?.split(' ')[0] || 'Meu Perfil') : 'Meu Perfil',
      code: '07'
    },
  ];

  const educationLinks = [
    { path: '/educacao/o-que-e-valuation', label: 'O que é Valuation?' },
    { path: '/educacao/fluxo-de-caixa-descontado', label: 'Fluxo de Caixa Descontado (DCF)' },
    { path: '/educacao/formula-de-graham', label: 'Fórmula de Benjamin Graham' },
    { path: '/educacao/metodo-peter-lynch', label: 'Método Peter Lynch' },
    { path: '/educacao/piotroski-f-score', label: 'Piotroski, Altman & Beneish' },
  ];

  return (
    <>
      {/* Backdrop & Institutional Ledger Drawer */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[10000] flex print:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeSidebar}
              className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            />

            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.22, ease: 'easeOut' }}
              className="relative h-full w-80 bg-[#FAF8F5] dark:bg-[#0A0C10] border-r border-[#E4E0D8] dark:border-[#1F2430] flex flex-col shadow-2xl"
            >
              {/* Drawer Header */}
              <div className="h-14 px-5 border-b border-[#E4E0D8] dark:border-[#1F2430] flex items-center justify-between bg-white dark:bg-[#11141C]">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-[#FAF8F5] dark:bg-[#0A0C10] rounded border border-[#E4E0D8] dark:border-[#1F2430] flex items-center justify-center">
                    <Logo className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-base tracking-tight text-[#111315] dark:text-[#F3F4F6] leading-none">
                      Valor Justo
                    </h2>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-[#5A6065] dark:text-[#8A94A6] mt-0.5">
                      INSTITUTIONAL LEDGER
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeSidebar}
                  className="w-8 h-8 flex items-center justify-center text-[#5A6065] hover:text-[#111315] dark:hover:text-[#F3F4F6] border border-transparent hover:border-[#E4E0D8] dark:hover:border-[#1F2430] rounded transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Primary Navigation */}
              <div className="flex-1 overflow-y-auto py-5 px-4 space-y-6">
                <div>
                  <div className="px-2 mb-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-[#5A6065] dark:text-[#8A94A6]">
                    Módulos do Terminal
                  </div>
                  <nav className="border border-[#E4E0D8] dark:border-[#1F2430] rounded bg-white dark:bg-[#11141C] divide-y divide-[#E4E0D8] dark:divide-[#1F2430] overflow-hidden">
                    {menuItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      const Icon = item.icon as any;

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={closeSidebar}
                          className={`w-full flex items-center justify-between px-3.5 py-3 transition-colors cursor-pointer ${
                            isActive
                              ? 'bg-[#111315] text-[#FAF8F5] dark:bg-[#F3F4F6] dark:text-[#0A0C10] font-semibold'
                              : 'text-[#111315] dark:text-[#F3F4F6] hover:bg-[#FAF8F5] dark:hover:bg-[#161A24]'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-5 h-5 flex items-center justify-center">
                              {item.icon === 'avatar' ? (
                                <img
                                  src={session.user_metadata.avatar_url}
                                  alt="Perfil"
                                  className="w-5 h-5 rounded-sm object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <Icon className={`w-4 h-4 ${isActive ? 'text-current' : 'text-[#5A6065] dark:text-[#8A94A6]'}`} />
                              )}
                            </div>
                            <span className="text-xs tracking-tight">{item.label}</span>
                          </div>
                          <span className={`font-mono text-[10px] ${isActive ? 'opacity-80' : 'text-[#5A6065] dark:text-[#8A94A6]'}`}>
                            {item.code}
                          </span>
                        </Link>
                      );
                    })}
                  </nav>
                </div>

                {/* Educational Reference Index */}
                <div>
                  <div className="px-2 mb-2 flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-[#5A6065] dark:text-[#8A94A6]">
                    <BookOpen className="w-3 h-3" /> Metodologia & Modelos
                  </div>
                  <div className="border border-[#E4E0D8] dark:border-[#1F2430] rounded bg-white dark:bg-[#11141C] divide-y divide-[#E4E0D8] dark:divide-[#1F2430]">
                    {educationLinks.map((edu) => (
                      <Link
                        key={edu.path}
                        to={edu.path}
                        onClick={closeSidebar}
                        className="block px-3.5 py-2.5 text-xs text-[#5A6065] dark:text-[#8A94A6] hover:text-[#111315] dark:hover:text-[#F3F4F6] hover:bg-[#FAF8F5] dark:hover:bg-[#161A24] transition-colors"
                      >
                        {edu.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Drawer Footer */}
              <div className="p-4 border-t border-[#E4E0D8] dark:border-[#1F2430] bg-white dark:bg-[#11141C] flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#5A6065] dark:text-[#8A94A6]">
                  Suporte Técnico
                </span>
                <Link
                  to="/contato"
                  onClick={closeSidebar}
                  className="font-mono text-[11px] font-semibold text-[#0D7A5F] dark:text-[#10B981] hover:underline"
                >
                  Falar com Equipe →
                </Link>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
